import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Alert,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4001';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
function buildYears() { const y = new Date().getFullYear(); return [y - 2, y - 1, y, y + 1]; }

// ── Formatters ────────────────────────────────────────────────────────────────
const fc = (v: number | null | undefined, dec = 0) =>
  v == null ? '—' : Number(v).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });

const fmtPeriodStart = (year: number, month: number) =>
  new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
const fmtPeriodEnd = (year: number, month: number) =>
  new Date(year, month, 0).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
const fmtDayLabel = (iso: string) => {
  const d = new Date(iso + 'T00:00:00');
  return `${String(d.getDate()).padStart(2, '0')}-${MONTHS[d.getMonth()].substring(0,3)}-${String(d.getFullYear()).slice(2)}`;
};
const REPORT_DATE = new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' });

// ── Styles ────────────────────────────────────────────────────────────────────
const NAVY = '#1E2D78';
const TH: React.CSSProperties = {
  background: NAVY, color: '#fff', fontWeight: 700,
  border: '1px solid #0d1b57', padding: '6px 10px', fontSize: '10px',
  textAlign: 'center', whiteSpace: 'nowrap',
};
const TD: React.CSSProperties = {
  border: '1px solid #ccc', padding: '4px 10px', fontSize: '10px',
  textAlign: 'center', whiteSpace: 'nowrap', verticalAlign: 'middle',
};
const TD_R: React.CSSProperties = { ...TD, textAlign: 'right' };
const TD_L: React.CSSProperties = { ...TD, textAlign: 'left' };

// ── Signature block ───────────────────────────────────────────────────────────
const SigBlock: React.FC<{ label: string; name: string; title: string; date?: string }> = ({ label, name, title, date }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 200 }}>
    <Typography sx={{ fontSize: '10px' }}>{label}:</Typography>
    <Box sx={{ borderBottom: '1px solid #000', width: '100%', minHeight: 30 }} />
    <Typography sx={{ fontSize: '10px', fontWeight: 700, borderBottom: '1px solid #000', textAlign: 'center' }}>{name}</Typography>
    <Typography sx={{ fontSize: '10px', textAlign: 'center' }}>{title}</Typography>
    {date && <Typography sx={{ fontSize: '9px', color: '#555' }}>{date}</Typography>}
  </Box>
);

// ══════════════════════════════════════════════════════════════════════════════
// PAGE 1 — Flared Gas Billing Invoice
// ══════════════════════════════════════════════════════════════════════════════
const FlaredGasBillingInvoice: React.FC<{
  year: number; month: number;
  totalFlared: number; deductible: number; payableByDole: number;
  unitPrice: number; totalAmount: number;
}> = ({ year, month, totalFlared, deductible, payableByDole, unitPrice, totalAmount }) => {
  return (
    <Box sx={{ p: '16mm 18mm', fontFamily: 'Arial, sans-serif', width: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <Typography sx={{ fontWeight: 800, fontSize: '12px', mb: 0.5 }}>SURALLAH BIOGAS VENTURES CORP.</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 4, mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '10px' }}>Billed to: Dole Philippines, Inc.</Typography>
          <Typography sx={{ fontSize: '10px' }}>Billing re: Flared Gas - DPO Plant</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.25 }}>
            <Typography sx={{ fontSize: '10px' }}>Period Covered:</Typography>
            <Typography sx={{ fontSize: '10px' }}>{fmtPeriodStart(year, month)}</Typography>
            <Typography sx={{ fontSize: '10px' }}>to</Typography>
            <Typography sx={{ fontSize: '10px' }}>{fmtPeriodEnd(year, month)}</Typography>
          </Box>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '10px' }}>Date:&nbsp;&nbsp;&nbsp;&nbsp;{REPORT_DATE}</Typography>
        </Box>
      </Box>

      {/* Supporting calculation */}
      <Typography sx={{ fontWeight: 700, fontSize: '10px', mb: 1, mt: 3, textDecoration: 'underline' }}>SUPPORTING CALCULATION FOR:</Typography>

      <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 20, marginTop: 12 }}>
        <thead>
          <tr>
            <th style={{ ...TH, textAlign: 'left', width: '22%' }}>Gas Particulars</th>
            <th style={TH}>Flared Volume</th>
            <th style={TH}>Volume Deductible<br/>to SBVC</th>
            <th style={TH}>Volume Payable by<br/>Dole</th>
            <th style={{ ...TH, width: '8%' }}>Units</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={TD_L}>Flared Gas</td>
            <td style={TD_R}>{fc(totalFlared, 2)}</td>
            <td style={TD}>{deductible > 0 ? fc(deductible, 2) : '-'}</td>
            <td style={TD_R}>{fc(payableByDole, 2)}</td>
            <td style={TD}>Nm3</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} style={{ ...TH, textAlign: 'left', fontWeight: 800 }}>Total Flared:</td>
            <td style={{ ...TH, textAlign: 'right', fontWeight: 800 }}>{fc(payableByDole, 2)}</td>
            <td style={{ ...TH, fontWeight: 800 }}>Nm3</td>
          </tr>
        </tfoot>
      </table>

      {/* Amount summary */}
      <Box sx={{ mt: 4, ml: 0, maxWidth: 400 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
          <Typography sx={{ fontSize: '10px' }}>Flared Gas</Typography>
          <Typography sx={{ fontSize: '10px' }}>{fc(payableByDole)} Nm3</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{ fontSize: '10px' }}>Unit Price</Typography>
          <Typography sx={{ fontSize: '10px' }}>{unitPrice} Php/Nm3</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', background: '#FFD700', p: '6px 10px', border: '1px solid #bbb' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '10px' }}>Total Amount Due</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '10px' }}>{fc(totalAmount, 2)}&nbsp;Php</Typography>
        </Box>
      </Box>

      {/* Signatures */}
      <Box sx={{ mt: 8, display: 'flex', gap: 6 }}>
        <SigBlock label="Prepared By" name="Francis David A. Requillo" title="SBVC Representative" />
        <SigBlock label="Approved By" name="Veerland Dongon" title="SBVC Representative" />
      </Box>
      <Box sx={{ mt: 5 }}>
        <SigBlock label="Received By" name="Toribio G. Castro" title="Dole Representative" date={REPORT_DATE} />
      </Box>
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// PAGE 2 — Daily Gas Flare Monitoring Table
// ══════════════════════════════════════════════════════════════════════════════
const FlareMonitoringTable: React.FC<{
  rows: any[];
  year: number; month: number;
  totalFlared: number; deductible: number; payableByDole: number;
}> = ({ rows, year, month, totalFlared, deductible, payableByDole }) => {
  // Build all days of month (1..N)
  const daysInMonth = new Date(year, month, 0).getDate();
  const allDays: { date: string; flaredVolume: number | null }[] = [];
  const rowMap: Record<string, number | null> = {};
  for (const r of rows) {
    rowMap[r.readingDate] = r.gasFlareTotalVolume ?? null;
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const date = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    allDays.push({ date, flaredVolume: rowMap[date] ?? null });
  }

  return (
    <Box sx={{ p: '12mm 16mm', fontFamily: 'Arial, sans-serif', width: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Box sx={{ border: '2px solid ' + NAVY, px: 1.5, py: 0.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '10px', color: NAVY, lineHeight: 1.2 }}>SURALLAH BIOGAS</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '10px', color: NAVY, lineHeight: 1.2 }}>VENTURES CORP.</Typography>
        </Box>
        <Box sx={{ textAlign: 'center', flex: 1, mx: 3 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '13px' }}>SURALLAH BIOGAS PLANT</Typography>
          <Typography sx={{ fontSize: '10px' }}>Gas Flare Monitoring</Typography>
          <Typography sx={{ fontSize: '10px', fontWeight: 700 }}>{MONTHS[month - 1].toUpperCase()} {year}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LocalFireDepartmentIcon sx={{ fontSize: 16, color: NAVY }} />
          <Typography sx={{ fontSize: '9px', color: '#555', fontWeight: 600 }}>METPower</Typography>
        </Box>
      </Box>

      {/* Table */}
      <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 8 }}>
        <thead>
          <tr>
            <th style={{ ...TH, textAlign: 'left', width: '22%' }}>Date</th>
            <th style={TH}>Flared Volume</th>
            <th style={TH}>Volume Chargeable<br/>to SBVC</th>
            <th style={TH}>Volume Payable<br/>by Dole</th>
          </tr>
        </thead>
        <tbody>
          {allDays.map(day => {
            const hasData = day.flaredVolume != null && day.flaredVolume > 0;
            return (
              <tr key={day.date}>
                <td style={TD_L}>{fmtDayLabel(day.date)}</td>
                <td style={TD_R}>{hasData ? fc(day.flaredVolume!, 0) : '-'}</td>
                <td style={TD}></td>
                <td style={TD_R}>{hasData ? fc(day.flaredVolume!, 0) : '-'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Summary section */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '10px' }}>
          <tbody>
            <tr>
              <td style={{ padding: '2px 12px', fontWeight: 700, textAlign: 'right' }}>Total Flared Volume</td>
              <td style={{ padding: '2px 12px', fontWeight: 700, textAlign: 'right' }}>{fc(totalFlared, 2)}</td>
              <td style={{ padding: '2px 6px' }}>Nm3</td>
            </tr>
            <tr>
              <td style={{ padding: '2px 12px', fontWeight: 700, textAlign: 'right' }}>Total Deducted Volume</td>
              <td style={{ padding: '2px 12px', fontWeight: 700, textAlign: 'right' }}>{deductible > 0 ? fc(deductible, 2) : '-'}</td>
              <td style={{ padding: '2px 6px' }}>Nm3</td>
            </tr>
            <tr>
              <td style={{ padding: '2px 12px', fontWeight: 700, textAlign: 'right' }}>Total Payable by Dole</td>
              <td style={{ padding: '2px 12px', fontWeight: 700, textAlign: 'right' }}>{fc(payableByDole, 2)}</td>
              <td style={{ padding: '2px 6px' }}>Nm3</td>
            </tr>
          </tbody>
        </table>
      </Box>

      {/* Signatures */}
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: '10px', mb: 0.5 }}>Prepared by:</Typography>
        <Box sx={{ mt: 3 }}>
          <Box sx={{ borderTop: '1px solid #000', width: 200, pt: 0.25 }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 700, textAlign: 'center' }}>Francis David Requillo</Typography>
            <Typography sx={{ fontSize: '10px', textAlign: 'center' }}>SBVC</Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: '10px', mb: 0.5 }}>Reviewed and Checked by:</Typography>
        <Box sx={{ mt: 3 }}>
          <Box sx={{ borderTop: '1px solid #000', width: 200, pt: 0.25 }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 700, textAlign: 'center' }}>Veerland Dongon</Typography>
            <Typography sx={{ fontSize: '10px', textAlign: 'center' }}>SBVC</Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Box>
          <Typography sx={{ fontSize: '10px', mb: 0.5 }}>Confirmed By:</Typography>
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '9px', color: '#555' }}>{REPORT_DATE}</Typography>
            <Box sx={{ borderTop: '1px solid #000', width: 200, pt: 0.25, mt: 0.5 }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 700 }}>Dole Representative</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// Main Component
// ══════════════════════════════════════════════════════════════════════════════
const FlaredGasReport: React.FC = () => {
  const navigate = useNavigate();
  const [repMonth, setRepMonth] = useState(new Date().getMonth() + 1);
  const [repYear,  setRepYear]  = useState(new Date().getFullYear());
  const [biogasReport, setBiogasReport] = useState<any>(null);
  const [effectiveRates, setEffectiveRates] = useState<{ power: number; gas: number }>({ power: 7.5, gas: 13.5 });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const load = useCallback(async (year: number, month: number) => {
    setLoading(true); setError('');
    const ym = `${year}-${String(month).padStart(2, '0')}`;
    try {
      const [bioRes, ratesRes] = await Promise.all([
        axios.get(`${API}/daily-biogas/monthly-report`, { params: { month: ym } }),
        axios.get(`${API}/billing-rates/effective`, { params: { month: ym } }).catch(() => ({ data: { power: 7.5, gas: 13.5 } })),
      ]);
      setBiogasReport(bioRes.data);
      setEffectiveRates(ratesRes.data ?? { power: 7.5, gas: 13.5 });
    } catch { setError('Failed to load report data.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(repYear, repMonth); }, [repYear, repMonth, load]);

  const rows: any[] = biogasReport?.rows ?? [];
  const totalFlared = rows.reduce((s: number, r: any) => s + (r.gasFlareTotalVolume ?? 0), 0);
  const deductible = 0; // currently no deductions
  const payableByDole = totalFlared - deductible;
  const gasBillingRate = effectiveRates.gas;
  const totalAmount = payableByDole * gasBillingRate;

  const pageStyle = {
    mb: 2, overflow: 'hidden',
    '@media print': { boxShadow: 'none', border: 'none', mb: 0 },
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Controls (screen only) ── */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', '@media print': { display: 'none' } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} size="small" variant="outlined">Back</Button>
        <LocalFireDepartmentIcon sx={{ color: '#e65100', fontSize: 28 }} />
        <Typography variant="h6" fontWeight={700} color="#e65100">Flared Gas Monitoring Report</Typography>
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Month</InputLabel>
          <Select value={repMonth} label="Month" onChange={e => setRepMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <MenuItem key={i} value={i + 1}>{m}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <InputLabel>Year</InputLabel>
          <Select value={repYear} label="Year" onChange={e => setRepYear(Number(e.target.value))}>
            {buildYears().map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}
          sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}>
          Print / Save PDF
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, '@media print': { display: 'none' } }}>{error}</Alert>}
      {rows.length === 0 && !loading && !error && (
        <Alert severity="info" sx={{ mb: 2, '@media print': { display: 'none' } }}>
          No daily monitoring data for {MONTHS[repMonth - 1]} {repYear}.
          Enter gas flare data in the Daily Biogas Monitor to generate this report.
        </Alert>
      )}

      {/* ── PAGE 1: Billing Invoice ── */}
      <Paper elevation={2} className="flare-billing-page" sx={pageStyle}>
        <FlaredGasBillingInvoice
          year={repYear} month={repMonth}
          totalFlared={totalFlared} deductible={deductible} payableByDole={payableByDole}
          unitPrice={gasBillingRate} totalAmount={totalAmount}
        />
      </Paper>

      {/* ── PAGE 2: Daily Monitoring Table ── */}
      <Paper elevation={2} className="flare-monitoring-page" sx={pageStyle}>
        <FlareMonitoringTable
          rows={rows}
          year={repYear} month={repMonth}
          totalFlared={totalFlared} deductible={deductible} payableByDole={payableByDole}
        />
      </Paper>

      {/* Print styles */}
      <style>{`
        @media screen {
          .flare-billing-page, .flare-monitoring-page { zoom: 1.4; margin-bottom: 40px; }
        }
        @media print {
          @page { size: A4 portrait; margin: 12mm 14mm; }
          .flare-billing-page, .flare-monitoring-page {
            zoom: 1 !important;
            margin: 0 !important;
            box-shadow: none !important;
            border: none !important;
            overflow: visible !important;
            break-after: page;
          }
          .flare-monitoring-page:last-of-type { break-after: avoid; }
          .MuiDrawer-root, .MuiAppBar-root, header, nav { display: none !important; }
        }
      `}</style>
    </Box>
  );
};

export default FlaredGasReport;
